/**
 * tutorialData.js — All tutorial content for Healist.AI
 *
 * DATA-DRIVEN: Adding a new tutorial = append an object to TUTORIALS.
 * Adding a new category = append to TUTORIAL_CATEGORIES.
 * Zero component changes needed.
 */

export const TUTORIAL_CATEGORIES = [
  { id: 'getting-started', label: 'Getting Started', icon: '\u{1F680}', order: 1 },
  { id: 'nutrition',       label: 'Nutrition',        icon: '\u{1F957}', order: 2 },
  { id: 'health',          label: 'Health & Medical',  icon: '\u{1F3E5}', order: 3 },
  { id: 'fitness',         label: 'Fitness',           icon: '\u{1F4AA}', order: 4 },
  { id: 'devices',         label: 'Devices',           icon: '\u{231A}', order: 5 },
  { id: 'digital-twin',    label: 'Digital Twin',       icon: '\u{1F9EC}', order: 6 },
  { id: 'longevity',       label: 'Longevity',          icon: '\u{2728}', order: 7 },
  { id: 'ai-features',     label: 'AI Features',       icon: '\u{1F916}', order: 8 },
  { id: 'account',         label: 'Account & Settings', icon: '\u{1F464}', order: 9 },
];

export const TUTORIALS = [
  // ===================== GETTING STARTED =====================
  {
    id: 'first-login',
    title: 'First Login & PWA Install',
    icon: '\u{1F4F1}',
    category: 'getting-started',
    order: 2,
    estimatedMinutes: 3,
    description: 'Learn how to log in, install Healist as an app on your phone, and navigate the sidebar menu.',
    prerequisites: [],
    relatedTutorials: ['profile-setup', 'dashboard-tour'],
    tags: ['login', 'pwa', 'install', 'mobile', 'navigation', 'sidebar'],
    steps: [
      {
        title: 'Open Healist in your browser',
        description: 'Navigate to healist.ai on your mobile device or desktop browser. Healist works on any modern browser \u2014 Chrome, Safari, Firefox, or Edge.',
        tip: null,
        warning: null,
      },
      {
        title: 'Log in with your credentials',
        description: 'Enter your email address and password on the login screen. During the invite-only beta, a new account is created by tapping "Sign Up" and entering the email your invitation was sent to — that advances you to the signup form. An email that is not on the invite list is not recognised; you can request an invite at healist.ai/beta.',
        tip: 'Use a strong, unique password. Healist stores sensitive health information protected under HIPAA.',
        warning: null,
      },
      {
        title: 'Install as a PWA (mobile)',
        description: 'On iOS Safari, tap the Share button (square with arrow) then "Add to Home Screen." On Android Chrome, tap the three-dot menu then "Add to Home Screen." This installs Healist as a full-screen app.',
        tip: null,
        warning: 'iPhone/iPad: after adding Healist to your Home Screen, you MUST open it from that Home Screen icon before turning on notifications. Turning them on while Healist is open in a Safari tab will not work — Apple only allows notifications for the installed app. This is an Apple rule, not a Healist setting.',
      },
      {
        title: 'Turn on notifications (iPhone/iPad)',
        description: 'Close Safari. Tap the Healist icon on your Home Screen to open the app. Then go to Account → Settings → Enable Notifications and allow them when your phone asks. Use "Send Test" to confirm one arrives.',
        tip: 'If you don\'t see the prompt, notifications may already be on for this device — check Account → Settings.',
        warning: 'Doing this from a Safari tab instead of the Home Screen icon silently does nothing. If notifications never arrive, this is almost always the reason.',
      },
      {
        title: 'Explore the sidebar navigation',
        description: 'The sidebar has four sections: HOME (Dashboard, Analytics & Reporting), WELLNESS (Nutrition, Fitness, Devices), MEDICAL (Health, Digital Twin, Longevity, Medications, Records), and MY ACCOUNT (Settings, Account, Tutorials). Tap any item to navigate.',
        tip: null,
        warning: null,
      },
      {
        title: 'Try the AI Consult button',
        description: 'At the top of the sidebar, above the HOME section, you\'ll see the "AI Consult" button. This opens the AI Assistant Hub where you can access all AI-powered features in one place.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand the page layout',
        description: 'Each page has a navy gradient header bar with a "\u2190 Dashboard" button that returns you to the Dashboard. Some pages have pill toolbars (quick action buttons) and tab bars (section navigation).',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Healist is a Progressive Web App (PWA). It works on all devices through the browser \u2014 no app store download required (except for Apple Health sync, which uses the companion iOS app).' },
    ],
  },
  {
    id: 'profile-setup',
    title: 'Set Up Your Profile',
    icon: '\u{1F9D1}',
    category: 'getting-started',
    order: 3,
    estimatedMinutes: 3,
    description: 'Complete your profile with personal details that power AI-driven health recommendations.',
    prerequisites: ['first-login'],
    relatedTutorials: ['body-characteristics', 'food-preferences'],
    tags: ['profile', 'name', 'dob', 'gender', 'setup'],
    steps: [
      {
        title: 'Navigate to your profile',
        description: 'Go to Health in the sidebar, then select the "Body" tab. Your profile information is displayed at the top of this page.',
        tip: null,
        warning: null,
      },
      {
        title: 'Enter your basic information',
        description: 'Add your name, date of birth, and birth gender. These are used by AI features to calculate age-appropriate recommendations (e.g., BMR, nutrient targets).',
        tip: 'Birth gender is used for clinical calculations like BMR and body composition formulas. You can select "Prefer not to say" if desired.',
        warning: null,
      },
      {
        title: 'Upload a profile picture',
        description: 'Tap the avatar circle to upload a profile photo. This appears in the sidebar and throughout the app.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review your AI Data Set',
        description: 'Go to Records \u2192 My UHC Data Set to see the full data set the AI uses to personalize your recommendations. It updates automatically whenever anything changes in Healist.',
        tip: 'The more complete your profile, the better your AI recommendations will be. Empty fields mean the AI has to make assumptions.',
        warning: null,
      },
      {
        title: 'Understand how profile data drives AI',
        description: 'Every AI feature (recipes, meal plans, fitness programs, chat) uses your Unified Context \u2014 a comprehensive health profile built from your profile, body stats, allergies, medications, devices, and more.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'body-characteristics',
    title: 'Enter Body Characteristics',
    icon: '\u{1F4CF}',
    category: 'getting-started',
    order: 4,
    estimatedMinutes: 3,
    description: 'Record your height, weight, and body composition so AI features can calculate accurate health metrics.',
    prerequisites: ['profile-setup'],
    relatedTutorials: ['health-tracking', 'healist-score'],
    tags: ['body', 'height', 'weight', 'bmi', 'composition'],
    steps: [
      {
        title: 'Navigate to Body Characteristics',
        description: 'Go to Health in the sidebar, then select the "Body" tab. Your body characteristics form is here.',
        tip: null,
        warning: null,
      },
      {
        title: 'Enter height and weight',
        description: 'Input your current height and weight. BMI is calculated automatically. These values feed into calorie calculations, fitness program design, and nutrition targets.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add body composition (optional)',
        description: 'If you know your body fat percentage, muscle mass, or other composition metrics, enter them here. Connected smart scales can sync these automatically via Apple Health or Google Health Connect.',
        tip: 'Don\'t worry if you don\'t know your body fat percentage. The AI works with whatever data you provide.',
        warning: null,
      },
      {
        title: 'View BMI calculation',
        description: 'Your BMI is calculated and displayed with a color-coded indicator. The calculation uses the standard formula: weight (kg) / height (m)\u00B2.',
        tip: null,
        warning: null,
      },
      {
        title: 'Track changes over time',
        description: 'Each time you update your body characteristics, the previous values are saved to your health history. View trends in Health \u2192 History tab.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Body characteristics are updated in your Unified Context immediately, so AI recommendations reflect your latest measurements right away.' },
    ],
  },
  {
    id: 'health-objectives',
    title: 'Create Health Objectives',
    icon: '\u{1F3AF}',
    category: 'getting-started',
    order: 5,
    estimatedMinutes: 3,
    description: 'Set health goals that guide every AI recommendation \u2014 from meal plans to fitness programs.',
    prerequisites: ['profile-setup'],
    relatedTutorials: ['diet-compliance', 'healist-score'],
    tags: ['objectives', 'goals', 'targets', 'health goals'],
    steps: [
      {
        title: 'Navigate to Objectives',
        description: 'Go to Health in the sidebar, then select the "Objectives" tab. This is where you define your health goals.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add 2\u20133 health objectives',
        description: 'Create objectives like "Lose 10 lbs," "Lower cholesterol," or "Build muscle." Each objective should be specific and measurable.',
        tip: 'Start with 2\u20133 objectives. Too many goals can dilute the AI\'s focus. You can always add more later.',
        warning: null,
      },
      {
        title: 'Set target values and dates',
        description: 'For each objective, set a target value (e.g., target weight) and an optional target date. This helps the AI create time-appropriate plans.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand how objectives drive AI',
        description: 'Your objectives are included in every AI prompt. When you generate a meal plan, the AI considers your goals. When you ask for fitness programs, it designs them around your objectives.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review objective progress',
        description: 'Progress lives in Analytics & Reporting → Objectives, not on the Health tab. Health is where you SET goals; Analytics is where you WATCH them. Each goal shows your starting point, where you are now, your target, and a chart of the trend.',
        tip: 'Every number shows where it came from — which device or record, and how many readings it is based on.',
        warning: null,
      },
      {
        title: 'Progress is measured, never typed in',
        description: 'Healist computes progress from your own health data — your watch, connected devices, lab results and records. You do not enter it by hand, so the number you see is what actually happened.',
        tip: 'If a goal says it is not being tracked yet, it needs data that has not arrived. Watch-based goals need HealistHealthSync installed.',
        warning: null,
      },
      {
        title: 'Choose what happens when you reach a goal',
        description: 'When you hit a target, Healist asks what to do next: "Keep tracking" leaves it active so you hear about it if the number drifts back — right for things you maintain, like blood pressure. "Mark done" files it with its full history — right for goals with a finish line.',
        tip: 'You can answer straight from the notification, or later on the goal itself in Analytics → Objectives. If you never answer, Healist keeps tracking it.',
        warning: null,
      },
      {
        title: 'Suggestions Healist finds for you',
        description: 'Healist watches the data you already share and occasionally suggests a goal — a number drifting from your usual range, or a run of unusual readings. Suggestions appear at the top of Health → Health Objectives and show their working: the actual numbers, the period, and how many readings they are based on.',
        tip: 'A suggestion changes nothing until you accept it. Declining costs nothing, and Healist will not raise the same measurement again for about three months.',
        warning: null,
      },
      {
        title: 'Find and restart archived goals',
        description: 'Archiving files a goal, it never deletes anything. See them under Analytics & Reporting → Objectives → Archived, with their complete history. Reactivate starts a goal again as a fresh copy, leaving the original record untouched.',
        tip: 'A restarted goal measures from where you are today, not from your old starting point — so your progress reflects your current health.',
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'food-preferences',
    title: 'Set Food Preferences & Allergies',
    icon: '\u{26A0}\u{FE0F}',
    category: 'getting-started',
    order: 6,
    estimatedMinutes: 2,
    description: 'Configure dietary restrictions and allergies so every AI recipe, meal plan, and product check respects your needs.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-recipe-generator', 'family-allergen-guard', 'nutrition-comparison'],
    tags: ['allergies', 'dietary', 'restrictions', 'vegan', 'gluten-free', 'preferences'],
    steps: [
      {
        title: 'Navigate to Food Preferences',
        description: 'Go to Nutrition in the sidebar. Your food preferences and allergy settings are accessible from the pill toolbar at the top of the page.',
        tip: null,
        warning: null,
      },
      {
        title: 'Set dietary restrictions',
        description: 'Select any dietary patterns you follow: vegetarian, vegan, gluten-free, dairy-free, keto, paleo, halal, kosher, and more. Multiple selections are supported.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add specific allergies',
        description: 'List any food allergies (e.g., peanuts, shellfish, eggs). These are flagged in AI recipes, meal plans, product comparisons, and the Family Allergen Guard.',
        tip: null,
        warning: 'Food allergy information is critical for safety. Double-check your entries \u2014 AI recipes and meal plans will exclude these ingredients.',
      },
      {
        title: 'Save and verify',
        description: 'Save your preferences. They are immediately included in your Unified Context and will influence all future AI-generated content.',
        tip: 'You can update preferences anytime. Changes take effect immediately for the next AI request.',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Food preferences also power the "Check Product" feature in Family Allergen Guard and the allergen warnings in Nutrition Comparison Mode.' },
    ],
  },

  {
    id: 'credits-explained',
    title: 'Credits — How AI Actions Are Priced',
    icon: '\u{1FA99}',
    category: 'getting-started',
    order: 7,
    estimatedMinutes: 4,
    description: 'Understand the credit system: what costs credits, what is always free, how daily credits reset, and what each plan includes.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-assistant-hub', 'data-privacy'],
    tags: ['credits', 'pricing', 'plan', 'limits', 'trial', 'upgrade', 'billing'],
    steps: [
      {
        title: 'Browsing is always free',
        description: 'Navigating Healist, opening pages, reading your own data, and viewing results you already generated NEVER cost credits. Credits are spent only when you ask the AI to generate something new.',
        tip: 'Every credit-spending button shows its cost up front as a small chip — for example "Rank my interventions +3" or "Generate AI Trend Analysis +1". Free actions show "FREE".',
        warning: null,
      },
      {
        title: 'Find your credit balance',
        description: 'Your remaining credits appear at the bottom of the sidebar, just above your name. Tap that chip to open Account, where you can see today\'s spending broken down action by action.',
        tip: null,
        warning: null,
      },
      {
        title: 'Costs scale with the work involved',
        description: 'Light actions cost 1 credit (a quick AI question, a nutrition analysis, a trend analysis, a medical-record extraction). Mid-weight actions cost 2–3 (generate or optimize a recipe, food or supplement recommendations, a biological-age assessment). Large generations cost the most — a fitness program is 12, and meal plans are 15 for 3 days, 35 for 7 days, and 80 for 14 days.',
        tip: 'AI Health Advisor chat is priced by depth, not by message: a quick question costs 1, an in-depth answer 3, and a deep analysis across your full health context 6. Healist routes your question to the right model automatically — you do not choose.',
        warning: null,
      },
      {
        title: 'Expensive actions ask first',
        description: 'Any action costing 4 or more credits — or any action at all when your balance is nearly gone — shows a confirmation first, stating the cost and how many credits you will have left afterward. Nothing expensive is ever spent by accident.',
        tip: 'Viewing the result of an action you already paid for is always free, no matter how many times you come back to it.',
        warning: null,
      },
      {
        title: 'Daily credits reset every night',
        description: 'Your daily allowance resets at midnight in your own local timezone. Unused daily credits do not roll over to the next day.',
        tip: null,
        warning: null,
      },
      {
        title: 'Know what your plan includes',
        description: 'Daily credits by plan: Base 15, Pro 45, Max 90. Your free trial runs at Max-level 90 credits per day with FULL feature access — including Digital Twin and Longevity Intelligence — so you can try everything, including a full 14-day meal plan, before deciding. Base does not include Digital Twin or Longevity. Pro adds Digital Twin Basic plus Longevity assessment, ranking and guidance. Max adds Digital-Twin simulation and the Longevity Simulate features.',
        tip: null,
        warning: 'Plan gates are firm. Buying a credit pack increases HOW MUCH you can do on your current plan — it never unlocks a feature belonging to a higher plan. To gain a feature, upgrade the plan.',
      },
      {
        title: 'If you run out mid-action',
        description: 'Purchased credit packs never expire, and they are used automatically once your daily credits are gone. If you run out partway through something, Healist grants a one-time daily courtesy top-up so you can finish the action you started instead of hitting a wall.',
        tip: null,
        warning: null,
      },
      {
        title: 'A failed action gives its credits back',
        description: 'If an AI action fails and produces nothing — the AI service is unavailable, a photo cannot be read, or a meal plan or fitness program stops before it is created — the credits it used are returned automatically, to the same place they came from (daily allowance, credit pack, or courtesy top-up).',
        tip: 'An action that produces a partial result, such as a meal plan with a few meals still to fill, keeps its cost: you still get the plan.',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'A few actions are always free and never touch your balance — the connection greeting, the domain check that runs before recipe actions, and Longevity auto-recalibration detection.' },
    ],
  },

  {
    // #64 finalization (2026-08-09). The beta cohort AGREES to give feedback in exchange for
    // free access — it is one of the two consent checkboxes at sign-up — and the 🧪 button sits
    // on every screen, yet nothing taught testers how to use it or what makes a report useful.
    // Written against the live FeedbackModal: conversational refine → confirm card → ticket no.
    id: 'beta-feedback',
    title: 'Beta Program — How to Send Feedback',
    icon: '\u{1F9EA}',
    category: 'getting-started',
    order: 10,
    estimatedMinutes: 3,
    description: 'You are here to break things and tell us about it. How to file feedback in seconds, what makes a report genuinely useful, and what happens after you send it.',
    prerequisites: ['first-login'],
    relatedTutorials: ['credits-explained', 'data-privacy'],
    tags: ['beta', 'feedback', 'bug', 'report', 'ticket', 'suggestion', 'support'],
    steps: [
      {
        title: 'The 🧪 button is always in the sidebar',
        description: 'Open the menu and look for "🧪 Send Beta Feedback" at the bottom, just above your name. It is there on every screen, so you never have to navigate somewhere special or remember where you were — open it the moment something looks wrong.',
        tip: 'File it while you are still on the screen where it happened. Healist captures the context automatically, so a report sent from the scene is worth far more than one written later from memory.',
        warning: null,
      },
      {
        title: 'Just describe it in your own words',
        description: 'Type what happened in plain language — "the meal plan button did nothing", "this number looks wrong", "I expected X and got Y". You do not need to categorise it, rate its severity, or write a formal bug report.',
        tip: null,
        warning: null,
      },
      {
        title: 'Healist asks a couple of follow-ups',
        description: 'A short back-and-forth sharpens your note into something actionable — what you expected, what you saw, whether it happens every time. Answer as briefly as you like; two words is fine.',
        tip: 'This is the same conversational engine used by the onboarding interview, so it should feel familiar.',
        warning: null,
      },
      {
        title: 'Check the summary, then send',
        description: 'Before anything is submitted you see a summary card with the title, category and severity Healist inferred. Tap "Send it" if that reads correctly, or "Edit" to keep refining. Nothing is filed until you approve it.',
        tip: null,
        warning: null,
      },
      {
        title: 'Keep your ticket number',
        description: 'Every accepted report comes back with a ticket number. Quote it in any follow-up — by email, or in a later piece of feedback — and we can pull up the exact context you were in.',
        tip: null,
        warning: null,
      },
      {
        title: 'What is most useful to us',
        description: 'Anything that surprised you. Wrong or nonsensical AI output, a number that does not match what you expected, a button that appears to do nothing, wording that confused you, or a step that felt like more work than it should be. "This felt clunky" is legitimate feedback — we would rather hear it than not.',
        tip: 'Small papercuts are worth reporting. They are the things that never get found in testing because the people testing already know the workarounds.',
        warning: null,
      },
      {
        title: 'Diagnostics, and how to pause them',
        description: 'Healist collects anonymous diagnostics during the beta — error traces and timings only, never your health data. At the bottom of the feedback window there is a checkbox to pause it if you would rather not send even that.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Feedback is treated as protected health information — it is encrypted, access-controlled and audit-logged, because a screenshot or description can easily contain health details.' },
      { type: 'info', text: 'Sending feedback is free. It never costs credits, no matter how long the conversation runs.' },
    ],
  },

  // ===================== NUTRITION =====================
  {
    id: 'pantry-management',
    title: 'Manage Your Pantry',
    icon: '\u{1F9C3}',
    category: 'nutrition',
    order: 1,
    estimatedMinutes: 5,
    description: 'Track pantry items with smart storage, expiry alerts, and automatic depletion when you cook.',
    prerequisites: ['first-login'],
    relatedTutorials: ['what-can-i-cook', 'shopping-lists', 'recipe-management'],
    tags: ['pantry', 'inventory', 'storage', 'expiry', 'food tracking'],
    steps: [
      {
        title: 'Navigate to the Pantry',
        description: 'Go to Nutrition in the sidebar, then select the "Pantry" tab. You\'ll see two sub-tabs: Items (your pantry list) and Inventory (analytics).',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a pantry item',
        description: 'Tap "Add Item" to add a new pantry item. Enter the name, quantity, and unit. The system automatically determines the best storage method (fridge, freezer, pantry) based on the item.',
        tip: 'The smart storage system uses keyword matching (e.g., "milk" \u2192 fridge, "frozen peas" \u2192 freezer). You can override the suggestion if needed.',
        warning: null,
      },
      {
        title: 'Understand expiry tracking',
        description: 'Expiration dates are calculated using FoodKeeper data from the USDA. Items approaching expiry appear with warning badges. Expired items are flagged in red.',
        tip: null,
        warning: null,
      },
      {
        title: 'Use search, sort, and filters',
        description: 'The pantry list supports search, sorting (by name, expiry, category), and filtering (by storage method, category, status). Use these to quickly find items.',
        tip: null,
        warning: null,
      },
      {
        title: 'Cook a recipe from your pantry',
        description: 'When you cook a recipe using "Cook This," pantry items are automatically depleted based on the recipe\'s ingredient list. Frozen items are auto-thawed.',
        tip: 'The matching system uses fuzzy matching \u2014 "chicken breast" in a recipe will match "chicken breasts" in your pantry.',
        warning: null,
      },
      {
        title: 'Check pantry against a shopping list',
        description: 'In Shopping Lists, use "Check Pantry" to see which ingredients you already have. Green badges mean you\'re stocked; red means you need to buy.',
        tip: null,
        warning: null,
      },
      {
        title: 'View inventory analytics',
        description: 'Switch to the "Inventory" sub-tab to see storage distribution, category breakdown, and expiry timeline for your pantry.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Pantry data feeds into "What Can I Cook?" and is included in your AI context for smarter meal planning.' },
    ],
  },
  {
    id: 'recipe-management',
    title: 'Create & Manage Recipes',
    icon: '\u{1F4D6}',
    category: 'nutrition',
    order: 2,
    estimatedMinutes: 4,
    description: 'Add recipes manually, upload from files, or let AI generate them. Organize, search, and cook from your collection.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-recipe-generator', 'pantry-management', 'shopping-lists'],
    tags: ['recipes', 'cookbook', 'upload', 'manual entry'],
    steps: [
      {
        title: 'Navigate to Recipes',
        description: 'Go to Nutrition in the sidebar, then select the "Recipes" tab. You\'ll see your recipe collection with search and sort controls.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a recipe manually',
        description: 'Tap "Add Recipe" and fill in the recipe name, ingredients, instructions, prep time, cook time, and servings. Nutrition info is optional.',
        tip: null,
        warning: null,
      },
      {
        title: 'Upload a recipe from a file',
        description: 'Use "Upload Recipe" to import a recipe from a file (PDF, image, or text). AI extracts the recipe details automatically using Claude.',
        tip: 'Photo uploads work well \u2014 take a picture of a recipe from a cookbook and the AI will extract all the details.',
        warning: null,
      },
      {
        title: 'View and edit recipes',
        description: 'Tap any recipe card to open the Recipe Viewer. From here you can edit details, view nutrition info, print, or delete the recipe.',
        tip: null,
        warning: null,
      },
      {
        title: 'Cook a recipe',
        description: 'Use the "Cook This" shortcut on any recipe card to mark it as cooked. This depletes matching pantry items automatically.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add recipe ingredients to shopping cart',
        description: 'From the recipe card or viewer, use "Add to Cart" to send all ingredients to your shopping cart. Choose to add to an existing list or create a new one, with a servings multiplier.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'ai-recipe-generator',
    title: 'Generate AI Recipes',
    icon: '\u{2728}',
    category: 'nutrition',
    order: 3,
    estimatedMinutes: 3,
    description: 'Get personalized recipes generated by AI based on your health profile, dietary needs, and preferences.',
    prerequisites: ['food-preferences'],
    relatedTutorials: ['recipe-management', 'xai-transparency'],
    tags: ['ai', 'recipe', 'generate', 'personalized'],
    steps: [
      {
        title: 'Open the AI Recipe Generator',
        description: 'Navigate to Nutrition, then use the pill toolbar to open the AI Recipe Generator. You can also access it from the AI Assistant Hub.',
        tip: null,
        warning: null,
      },
      {
        title: 'Describe what you want',
        description: 'Enter a description like "high-protein breakfast" or "Mediterranean dinner for two." The AI uses your health profile, allergies, and objectives to personalize the recipe.',
        tip: 'Be specific about cuisine, dietary needs, or ingredients you want to use. The more detail, the better the result.',
        warning: null,
      },
      {
        title: 'Review the generated recipe',
        description: 'The AI generates a complete recipe with ingredients, instructions, nutrition facts, and health notes explaining why this recipe suits your profile.',
        tip: null,
        warning: null,
      },
      {
        title: 'Citations & AI Reasoning',
        description: 'Tap "📚 Citations & AI Reasoning" to open the Citations tab listing the published sources behind the guidance, plus exactly what health data the AI used and the full prompt sent.',
        tip: null,
        warning: null,
      },
      {
        title: 'Save the recipe',
        description: 'If you like the recipe, tap "Save Recipe" to add it to your collection. Saved AI recipes appear in your Recipes tab alongside manually entered ones.',
        tip: 'You can also use "Optimize Recipe" on any manual or uploaded recipe to get AI-powered health improvements.',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'AI recipe generation uses your rate limit allocation. Check your remaining uses in Account \u2192 AI Usage.' },
    ],
  },
  {
    id: 'ai-meal-plan',
    title: 'Generate an AI Meal Plan',
    icon: '\u{1F4C5}',
    category: 'nutrition',
    order: 4,
    estimatedMinutes: 5,
    description: 'Create personalized weekly meal plans, activate them, and follow daily meal schedules.',
    prerequisites: ['food-preferences', 'health-objectives'],
    relatedTutorials: ['shopping-lists', 'pantry-management', 'xai-transparency'],
    tags: ['meal plan', 'weekly', 'daily meals', 'planning'],
    steps: [
      {
        title: 'Open the Meal Plan Generator',
        description: 'Go to Nutrition, then select the "Meal Plans" tab. Tap "Generate New Plan" to start.',
        tip: null,
        warning: null,
      },
      {
        title: 'Configure your meal plan',
        description: 'Set the number of days, meals per day, calorie targets, and any special requirements. The AI considers your health profile, allergies, objectives, and current pantry.',
        tip: null,
        warning: null,
      },
      {
        title: 'Wait for generation',
        description: 'Meal plan generation takes 30\u201390 seconds. A progress bar shows the current status with messages like "Building Day 3 of 7..."',
        tip: null,
        warning: null,
      },
      {
        title: 'Review the generated plan',
        description: 'Each day shows meals with recipes, ingredients, and nutrition totals. Tap any meal to see the full recipe details.',
        tip: null,
        warning: null,
      },
      {
        title: 'Citations & AI Reasoning',
        description: 'Each AI meal plan card has a "\ud83d\udcda Citations & AI Reasoning" button under the plan name. It opens the published sources behind the plan and the generation context \u2014 what health data the AI used to design it.',
        tip: null,
        warning: null,
      },
      {
        title: 'Activate the plan',
        description: 'Tap "Activate" to make this your current meal plan. The active plan drives your daily meal schedule on the Dashboard.',
        tip: 'Only one plan can be active at a time. Activating a new plan deactivates the previous one (it\'s still saved).',
        warning: null,
      },
      {
        title: 'Follow daily meals',
        description: 'With an active plan, the Dashboard shows today\'s meals. Mark meals as eaten or skipped. Generate shopping lists from the plan\'s ingredients.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'You can reactivate any past meal plan. Old plans are never deleted \u2014 they remain in your Meal Plans list.' },
    ],
  },
  {
    id: 'shopping-lists',
    title: 'Shopping Lists & Cart',
    icon: '\u{1F6D2}',
    category: 'nutrition',
    order: 5,
    estimatedMinutes: 4,
    description: 'Build shopping lists from recipes or meal plans, manage your cart, and hand off to Instacart.',
    prerequisites: ['first-login'],
    relatedTutorials: ['recipe-management', 'pantry-management', 'ai-meal-plan'],
    tags: ['shopping', 'cart', 'grocery', 'instacart', 'list'],
    steps: [
      {
        title: 'Navigate to Shopping Lists',
        description: 'Go to Nutrition, then select the "Shopping" tab. You\'ll see your current cart and any pending shopping lists.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add items from a recipe',
        description: 'From any recipe card, tap "Add to Cart." Choose to add to your existing pending list or create a new one. Set a servings multiplier to scale quantities.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add items from a meal plan',
        description: 'From an active meal plan, use "Generate Shopping List" to create a consolidated list of all ingredients needed for the plan.',
        tip: null,
        warning: null,
      },
      {
        title: 'Check against your pantry',
        description: 'Tap "Check Pantry" on your shopping list to see which items you already have. Green badges = in stock, red = need to buy.',
        tip: null,
        warning: null,
      },
      {
        title: 'Print or export',
        description: 'Use the Print button to print your shopping list with checkboxes for in-store use. The list prints in a clean, printer-friendly format.',
        tip: null,
        warning: null,
      },
      {
        title: 'Fulfill the cart',
        description: 'After shopping, tap "Mark as Fulfilled" to complete the list. Fulfilled carts are archived. To shop again, create a new cart.',
        tip: 'Each user has exactly one pending cart at a time. Fulfilling it allows you to start a fresh list.',
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'nutrition-comparison',
    title: 'Compare Products (NCM)',
    icon: '\u{1F50D}',
    category: 'nutrition',
    order: 6,
    estimatedMinutes: 3,
    description: 'Compare nutrition labels side-by-side using barcode scanning or manual search, with personalized Best4U recommendations.',
    prerequisites: ['food-preferences'],
    relatedTutorials: ['family-allergen-guard'],
    tags: ['compare', 'barcode', 'scan', 'nutrition label', 'ncm', 'best4u'],
    steps: [
      {
        title: 'Open Nutrition Comparison',
        description: 'Go to Nutrition, then use the pill toolbar to open Nutrition Comparison Mode (NCM).',
        tip: null,
        warning: null,
      },
      {
        title: 'Add products to compare',
        description: 'Enter a UPC barcode number or (on mobile) use the barcode scanner to scan a product. Add 2\u20134 products for side-by-side comparison.',
        tip: 'On desktop, enter the UPC number manually. On mobile, you\'ll see both a UPC field and a camera scan button.',
        warning: null,
      },
      {
        title: 'Review the comparison table',
        description: 'Products are displayed side-by-side with nutrition facts, ingredients, and allergen warnings. Values are color-coded for easy visual comparison.',
        tip: null,
        warning: null,
      },
      {
        title: 'Check the Best4U column',
        description: 'The "Best4U" column highlights which product is best for YOUR specific health profile, considering your allergies, objectives, and dietary preferences.',
        tip: null,
        warning: null,
      },
      {
        title: 'View allergen warnings',
        description: 'If any product contains ingredients matching your allergies, prominent warning badges appear. This uses the same allergy data as your food preferences.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'NCM uses the Nutritionix database for product lookups. Not all products may be available.' },
    ],
  },
  {
    id: 'family-allergen-guard',
    title: 'Family Allergen Guard',
    icon: '\u{1F6E1}\u{FE0F}',
    category: 'nutrition',
    order: 7,
    estimatedMinutes: 2,
    description: 'Check any product against your family members\' allergies to ensure food safety.',
    prerequisites: ['food-preferences'],
    relatedTutorials: ['nutrition-comparison'],
    tags: ['allergen', 'family', 'safety', 'check product', 'fag'],
    steps: [
      {
        title: 'Open Family Allergen Guard',
        description: 'Go to Nutrition, then use the pill toolbar to open Family Allergen Guard (FAG).',
        tip: null,
        warning: null,
      },
      {
        title: 'Check a product',
        description: 'Enter a product name or scan a barcode. The system checks the product\'s ingredients against all stored allergies for your household.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review safety results',
        description: 'Results show a clear safe/unsafe indicator for each family member, with specific allergen matches highlighted.',
        tip: null,
        warning: 'This feature is an aid, not a replacement for reading labels. Always verify ingredients directly for severe allergies.',
      },
      {
        title: 'Manage family members',
        description: 'Add family members with their specific allergies. Each person\'s allergies are checked independently when you scan a product.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'what-can-i-cook',
    title: 'What Can I Cook?',
    icon: '\u{1F373}',
    category: 'nutrition',
    order: 8,
    estimatedMinutes: 2,
    description: 'Get AI suggestions for meals you can make right now based on what\'s in your pantry.',
    prerequisites: ['pantry-management'],
    relatedTutorials: ['recipe-management', 'ai-recipe-generator'],
    tags: ['cook', 'pantry', 'suggestions', 'what to cook'],
    steps: [
      {
        title: 'Open What Can I Cook?',
        description: 'From the Dashboard Quick Actions, tap "What Can I Cook?" or find it in the Nutrition pill toolbar.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review AI suggestions',
        description: 'The AI analyzes your current pantry inventory and suggests recipes you can make with ingredients you already have, minimizing waste.',
        tip: 'Keep your pantry up to date for the most accurate suggestions. Items marked as expired are excluded.',
        warning: null,
      },
      {
        title: 'Cook from suggestions',
        description: 'Select a suggestion to view the full recipe. Use "Cook This" to prepare it \u2014 pantry items are automatically depleted.',
        tip: null,
        warning: null,
      },
      {
        title: 'See missing ingredients',
        description: 'Suggestions may note 1\u20132 missing ingredients that could be substituted or added to your shopping list.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'diet-compliance',
    title: 'Diet Compliance Check',
    icon: '\u{2705}',
    category: 'nutrition',
    order: 9,
    estimatedMinutes: 2,
    description: 'AI analysis of how well your actual diet aligns with your stated health objectives.',
    prerequisites: ['health-objectives'],
    relatedTutorials: ['healist-score', 'ai-meal-plan'],
    tags: ['diet', 'compliance', 'check', 'analysis'],
    steps: [
      {
        title: 'Open Diet Compliance Checker',
        description: 'From the Dashboard Quick Actions, tap "Diet Check." You can also access it from the Nutrition pill toolbar.',
        tip: null,
        warning: null,
      },
      {
        title: 'Run the analysis',
        description: 'The AI compares your recent meal logs, nutrition data, and food intake against your stated health objectives.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review compliance results',
        description: 'Results show an overall compliance score and breakdown by objective \u2014 where you\'re on track and where you\'re falling short.',
        tip: null,
        warning: null,
      },
      {
        title: 'Citations & AI Reasoning',
        description: 'Tap "📚 Citations & AI Reasoning" to open the published sources behind the recommendations, and see exactly what data the AI analyzed.',
        tip: 'Diet Compliance contributes 10% to your overall Healist Score.',
        warning: null,
      },
    ],
    notes: [],
  },

  // ===================== HEALTH & MEDICAL =====================
  {
    id: 'health-tracking',
    title: 'Track Health Data',
    icon: '\u{1F4CA}',
    category: 'health',
    order: 1,
    estimatedMinutes: 4,
    description: 'Monitor objectives progress, body changes, and health history over time.',
    prerequisites: ['body-characteristics', 'health-objectives'],
    relatedTutorials: ['healist-score', 'wearable-devices'],
    tags: ['tracking', 'history', 'trends', 'progress'],
    steps: [
      {
        title: 'Navigate to Health',
        description: 'Go to Health in the sidebar. The page has 5 tabs: Objectives, Body, Score, History, and Providers.',
        tip: null,
        warning: null,
      },
      {
        title: 'Track objectives progress',
        description: 'The Objectives tab shows each goal with current vs. target values, progress percentage, and trend direction.',
        tip: null,
        warning: null,
      },
      {
        title: 'Monitor body changes',
        description: 'The Body tab shows current body characteristics with historical comparison. See how weight, BMI, and composition change over time.',
        tip: null,
        warning: null,
      },
      {
        title: 'View your Healist Score',
        description: 'The Score tab shows your unified health score \u2014 a single number (0\u2013100) combining 8 health components with letter grades.',
        tip: null,
        warning: null,
      },
      {
        title: 'Browse health history',
        description: 'The History tab shows a timeline of all health data entries, including device syncs, manual entries, and AI assessments.',
        tip: null,
        warning: null,
      },
      {
        title: 'Use Trends & Analysis',
        description: 'Navigate to the Trends page (accessible from Dashboard) for detailed analytics with bar charts, date range selectors (7d/14d/30d/90d), and AI-powered analysis.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'medications-supplements',
    title: 'Medications & Supplements',
    icon: '\u{1F48A}',
    category: 'health',
    order: 2,
    estimatedMinutes: 6,
    description: 'Track prescriptions with OpenFDA autocomplete, manage supplements, view interactions and timing.',
    prerequisites: ['first-login'],
    relatedTutorials: ['medical-records', 'ai-assistant-hub'],
    tags: ['medications', 'supplements', 'prescriptions', 'interactions', 'drug', 'timing'],
    steps: [
      {
        title: 'Navigate to Medications',
        description: 'Go to Medications in the sidebar (under MEDICAL section). The page has 3 tabs: Prescriptions, Supplements, and Interactions.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a prescription',
        description: 'Tap "Add Prescription" and start typing the medication name. OpenFDA autocomplete suggests matching medications with both brand and generic names.',
        tip: 'The autocomplete searches both brand names and generic names in parallel. Select from the dropdown for accurate drug data.',
        warning: null,
      },
      {
        title: 'Set dosage and timing',
        description: 'Enter dosage, frequency, and timing (morning, afternoon, evening, bedtime). The timing engine uses this to build your daily medication schedule.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add supplements',
        description: 'Switch to the Supplements tab and tap "Add Supplement." Choose from a curated list of ~100 common supplements or enter a custom name.',
        tip: null,
        warning: null,
      },
      {
        title: 'Get AI supplement suggestions',
        description: 'Tap "AI Supplement Suggestions" (3 credits). The AI picks, from Healist\'s list of supplements with cited evidence, the ones that may fit your goals, and says why in one line. Each suggestion shows "📚 Citations" and "⚠️ Authority warnings" from NIH and other health authorities, and any interaction with your medications. Where an authority states an amount you see the recommended TOTAL daily intake from all sources (food included) and the upper limit; otherwise "Discuss the dose with your clinician".',
        tip: 'Adding a suggestion to My Supplements never fills in a dose for you — enter the dose you and your clinician choose.',
        warning: 'A recommended daily intake is not a supplement dose: it counts what you already get from food. For some nutrients (magnesium, for example) the upper limit for supplements is lower than the recommended total intake.',
      },
      {
        title: 'View interaction warnings',
        description: 'The Interactions tab shows drug-drug and drug-supplement interactions with severity levels: critical, significant, moderate, minor, and informational.',
        tip: null,
        warning: 'Always consult your healthcare provider about medication interactions. The interaction database covers ~60 common interactions but is not exhaustive.',
      },
      {
        title: 'Review timing schedule',
        description: 'The timing recommendation engine suggests optimal times for each medication and supplement, accounting for interactions (e.g., "take calcium 2 hours apart from thyroid medication").',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand food-drug interactions',
        description: 'When you add pantry items or cook recipes, the system checks for food-drug interactions (e.g., grapefruit + statins) and displays warning badges.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Medication data is included in your AI context. When you generate meal plans or recipes, the AI avoids foods that interact with your medications.' },
    ],
  },
  {
    id: 'medical-records',
    title: 'Upload Medical Records',
    icon: '\u{1F4C4}',
    category: 'health',
    order: 3,
    estimatedMinutes: 4,
    description: 'Upload PDF medical records for AI extraction of lab values, conditions, medications, and allergies.',
    prerequisites: ['first-login'],
    relatedTutorials: ['medications-supplements', 'health-tracking'],
    tags: ['medical records', 'upload', 'pdf', 'lab values', 'extraction'],
    steps: [
      {
        title: 'Navigate to Medical Records',
        description: 'Go to Records in the sidebar (under MEDICAL). The Health Records tab lists four ways to bring records in. For the beta, "Upload Medical Records" is the live one — "Sync from Mobile Health Platform", "Search All Providers" and "Connect Medicare" are marked COMING SOON and will activate in a later release.',
        tip: 'Uploading is not a lesser option — it feeds exactly the same AI extraction and Unified Health Context the automated importers will.',
        warning: null,
      },
      {
        title: 'Upload a medical document',
        description: 'Tap "Upload" and select a PDF file (lab results, discharge summaries, doctor\'s notes). Files are encrypted at rest using S3 SSE-KMS encryption.',
        tip: 'Both text-based and scanned PDFs are supported. Scanned documents use OCR (Textract) for text extraction.',
        warning: null,
      },
      {
        title: 'Wait for AI extraction',
        description: 'After upload, AI (Claude) extracts structured data: lab values, diagnoses, medications, allergies, and clinical notes. This takes 10\u201330 seconds.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review extracted data',
        description: 'View the extracted information organized by category. Verify accuracy \u2014 AI extraction is powerful but not perfect for all document formats.',
        tip: null,
        warning: null,
      },
      {
        title: 'View in My UHC Data Set',
        description: 'Extracted medical data flows into your Unified Health Context (UHC). Go to Records \u2192 My UHC Data Set to see how medical records contribute to your health profile.',
        tip: null,
        warning: null,
      },
      {
        title: 'Manage uploaded files',
        description: 'View, download, or delete uploaded records. The built-in PDF viewer shows documents inline without opening a new tab. Use the PDF toolbar to print.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Medical records are stored with HIPAA-compliant encryption. Only you can access your files. Deleting your account permanently removes all records.' },
    ],
  },
  {
    id: 'provider-coordination',
    title: 'Work with Healthcare Providers',
    icon: '\u{1F468}\u{200D}\u{2695}\u{FE0F}',
    category: 'health',
    order: 4,
    estimatedMinutes: 4,
    description: 'Add healthcare providers, send them reports, and receive their responses.',
    prerequisites: ['first-login'],
    relatedTutorials: ['medical-records', 'health-tracking'],
    tags: ['providers', 'doctors', 'reports', 'coordination', 'healthcare'],
    steps: [
      {
        title: 'Navigate to Providers',
        description: 'Go to Health in the sidebar, then select the "Providers" tab.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a healthcare provider',
        description: 'Tap "Add Provider" and enter their name, specialty, clinic, email, and phone number.',
        tip: null,
        warning: null,
      },
      {
        title: 'Generate a health report',
        description: 'Use "Generate Report" to create a comprehensive health summary that you can share with your provider. Reports include your health data, trends, and AI insights.',
        tip: null,
        warning: null,
      },
      {
        title: 'Send a report',
        description: 'Reports can be sent directly to your provider via the platform. They receive a secure link to view your health data.',
        tip: null,
        warning: null,
      },
      {
        title: 'View provider responses',
        description: 'When your provider sends back notes or recommendations, they appear in the Provider tab. These responses are incorporated into your health context.',
        tip: null,
        warning: null,
      },
      {
        title: 'Print or download reports',
        description: 'All reports can be downloaded as PDFs or printed directly from the viewer.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },

  // ===================== FITNESS =====================
  {
    id: 'ai-fitness-program',
    title: 'Generate a Fitness Program',
    icon: '\u{1F3CB}\u{FE0F}',
    category: 'fitness',
    order: 1,
    estimatedMinutes: 4,
    description: 'AI-powered fitness programs tailored to your goals, body type, and available equipment.',
    prerequisites: ['body-characteristics', 'health-objectives'],
    relatedTutorials: ['exercise-library', 'xai-transparency'],
    tags: ['fitness', 'workout', 'program', 'exercise', 'training'],
    steps: [
      {
        title: 'Navigate to Fitness Programs',
        description: 'Go to Fitness in the sidebar, then select the "Programs" tab.',
        tip: null,
        warning: null,
      },
      {
        title: 'Generate a new program',
        description: 'Tap "Generate Program" and configure: duration (weeks), frequency (days/week), focus areas, available equipment, and fitness level.',
        tip: 'Be honest about your fitness level \u2014 the AI designs progressions based on your stated experience.',
        warning: null,
      },
      {
        title: 'Wait for generation',
        description: 'Program generation takes 30\u201390 seconds with a self-calibrating progress bar. You\'ll see messages like "Building Week 2 of 4..."',
        tip: null,
        warning: null,
      },
      {
        title: 'Review the program',
        description: 'Each week shows daily workouts with exercises, sets, reps, and rest periods. Tap any exercise to see video demonstrations from the Exercise Library.',
        tip: null,
        warning: null,
      },
      {
        title: 'Track your progress',
        description: 'The program card shows a visual completion progress bar (current week / total weeks). Mark workouts as complete to advance.',
        tip: null,
        warning: null,
      },
      {
        title: 'Citations & AI Reasoning',
        description: 'Each AI program has a "\ud83d\udcda Citations & AI Reasoning" button. It opens the published sources behind the program and the generation context \u2014 your body stats, objectives, and fitness level that shaped its design.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Fitness programs use your rate limit allocation. Check remaining uses in Account \u2192 AI Usage.' },
    ],
  },
  {
    id: 'exercise-library',
    title: 'Browse the Exercise Library',
    icon: '\u{1F4DA}',
    category: 'fitness',
    order: 2,
    estimatedMinutes: 2,
    description: 'Explore 500+ exercises with video demonstrations, filterable by muscle group and equipment.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-fitness-program'],
    tags: ['exercises', 'library', 'videos', 'muscle group', 'equipment'],
    steps: [
      {
        title: 'Navigate to Exercise Library',
        description: 'Go to Fitness in the sidebar, then select the "Library" tab.',
        tip: null,
        warning: null,
      },
      {
        title: 'Browse or search',
        description: 'Use the search bar to find specific exercises, or browse by category. The library contains 500+ exercises with descriptions.',
        tip: null,
        warning: null,
      },
      {
        title: 'Filter by muscle group or equipment',
        description: 'Use filters to narrow results by target muscle group (chest, back, legs, etc.) or available equipment (dumbbells, barbell, bodyweight, etc.).',
        tip: null,
        warning: null,
      },
      {
        title: 'View exercise details',
        description: 'Tap any exercise to see detailed instructions, target muscles, and video demonstrations showing proper form.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'trainers-sports',
    title: 'Trainers & Sports',
    icon: '\u{26BD}',
    category: 'fitness',
    order: 3,
    estimatedMinutes: 3,
    description: 'Manage personal trainers, send them reports, and log sports and hobbies.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-fitness-program', 'provider-coordination'],
    tags: ['trainers', 'sports', 'hobbies', 'personal trainer'],
    steps: [
      {
        title: 'Navigate to Trainers',
        description: 'Go to Fitness in the sidebar, then select the "Trainers" tab.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a personal trainer',
        description: 'Tap "Add Trainer" and enter their name, specialty, contact info, and gym/studio. You can send them fitness reports just like healthcare providers.',
        tip: null,
        warning: null,
      },
      {
        title: 'Send trainer reports',
        description: 'Generate and send workout summaries, progress reports, and fitness metrics to your trainer for review.',
        tip: null,
        warning: null,
      },
      {
        title: 'Log sports & hobbies',
        description: 'Go to the "Sports & Hobbies" tab to log recreational activities like hiking, swimming, tennis, or yoga. These contribute to your activity tracking.',
        tip: null,
        warning: null,
      },
      {
        title: 'Track activity contributions',
        description: 'Logged sports and hobbies contribute to your Healist Score\'s Activity component (15% weight) alongside formal workout programs.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },

  // ===================== DEVICES =====================
  {
    id: 'wearable-devices',
    title: 'Connect Wearable Devices',
    icon: '\u{231A}',
    category: 'devices',
    order: 1,
    estimatedMinutes: 4,
    description: 'Sync data from Apple Watch, Fitbit, Garmin, WHOOP, Oura, and other wearables via Apple Health or Google Health Connect.',
    prerequisites: ['first-login'],
    relatedTutorials: ['digital-medical-devices', 'ios-health-sync', 'healist-score'],
    tags: ['wearable', 'apple watch', 'fitbit', 'garmin', 'whoop', 'oura', 'sync', 'health connect'],
    steps: [
      {
        title: 'Understand how device data flows',
        description: 'Your wearable sends its data to Apple Health on iPhone (or Google Health Connect on Android), and HealistHealthSync passes it along to Healist. HealistHealthSync is available for iPhone and iPad today. An Android version is coming soon \u2014 until then, wearable devices can\'t connect to Healist on Android.',
        tip: 'On iPhone, any device that works with Apple Health works with Healist \u2014 you don\'t need to connect each one separately.',
        warning: null,
      },
      {
        title: 'Ensure your wearable syncs to Apple Health or Health Connect',
        description: 'Most wearables sync to Apple Health or Google Health Connect automatically. Check your wearable\'s companion app settings to confirm health data sharing is enabled.',
        tip: null,
        warning: null,
      },
      {
        title: 'Install HealistHealthSync',
        description: 'Download HealistHealthSync from the Apple App Store. It works on iPhone and iPad. An Android version is coming soon — until then, wearable devices can\'t connect to Healist on Android.',
        tip: null,
        warning: null,
      },
      {
        title: 'View synced data',
        description: 'Device data surfaces in Analytics & Reporting (Avg Steps, Sleep, RHR, HRV and the Activity / Sleep / Vitals trend tabs) and feeds into your Healist Score and AI recommendations.',
        tip: null,
        warning: null,
      },
      {
        title: 'Check connected status',
        description: 'Go to Devices in the sidebar (under WELLNESS) to see which data sources are connected and when data was last synced. You can view data from any connected source.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Device data syncs automatically via HealistHealthSync in the background. Wearable data has a 0.75 trust score in the Data Origin Priority (DOP) system \u2014 higher than manual entry (0.60) but lower than medical devices (0.90).' },
    ],
  },
  {
    id: 'digital-medical-devices',
    title: 'Digital Medical Devices',
    icon: '\u{1FA7A}',
    category: 'devices',
    order: 2,
    estimatedMinutes: 3,
    description: 'Sync data from Dexcom CGM, Withings scales, and other medical devices via Apple Health or Google Health Connect.',
    prerequisites: ['wearable-devices'],
    relatedTutorials: ['health-tracking'],
    tags: ['dexcom', 'cgm', 'withings', 'medical device', 'glucose', 'health connect'],
    steps: [
      {
        title: 'How medical device data flows',
        description: 'Medical devices like Dexcom CGM and Withings scales send their readings to Apple Health on iPhone (or Google Health Connect on Android), and HealistHealthSync passes them to Healist automatically. An Android version of HealistHealthSync is coming soon — until then, these devices can\'t connect to Healist on Android.',
        tip: 'Any medical device that writes to Apple Health or Health Connect is supported \u2014 no direct connection to Healist needed.',
        warning: null,
      },
      {
        title: 'Ensure your device syncs to your health platform',
        description: 'Check your medical device\'s companion app (e.g., Dexcom G7, Withings Health Mate) to confirm it is sharing data with Apple Health or Google Health Connect.',
        tip: null,
        warning: null,
      },
      {
        title: 'View glucose analytics',
        description: 'If you use a Dexcom CGM, view Time in Range (TIR), Glucose Management Indicator (GMI), and coefficient of variation (CV%) in your Healist dashboard.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand data trust levels',
        description: 'Medical devices have a 0.90 trust score in the DOP system \u2014 higher than wearables (0.75). When medical device data conflicts with wearable data, the medical reading takes precedence.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Glucose context from Dexcom is included in AI meal plan and recipe generation \u2014 the AI can suggest low-glycemic meals based on your glucose patterns.' },
    ],
  },
  {
    id: 'ios-health-sync',
    title: 'HealistHealthSync iOS App',
    icon: '\u{F8FF}',
    category: 'getting-started',
    order: 1,
    estimatedMinutes: 5,
    description: 'Install the companion iOS app to sync 191 Apple Health data types and clinical records with your Healist account.',
    prerequisites: ['first-login'],
    relatedTutorials: ['wearable-devices'],
    tags: ['ios', 'apple health', 'healthkit', 'iphone', 'sync app', 'clinical records'],
    steps: [
      {
        title: 'Download from the App Store',
        description: 'Search for "HealistHealthSync" in the Apple App Store, or visit the download link from Settings \u2192 Devices.',
        tip: null,
        warning: null,
      },
      {
        title: 'Log in with your Healist account',
        description: 'Open the app and sign in with the same email and password you use on healist.ai.',
        tip: null,
        warning: null,
      },
      {
        title: 'Authorize Apple Health access',
        description: 'The app requests permission to read 191 HealthKit data types from Apple Health \u2014 including activity, body measurements, heart, nutrition, reproductive health, respiratory, sleep, vitals, and more.',
        tip: 'Grant access to all requested categories for the most complete health picture.',
        warning: null,
      },
      {
        title: 'Enable clinical records (optional)',
        description: 'If you have clinical records in Apple Health (from hospitals via Apple Health Records), HealistHealthSync can sync those too \u2014 lab results, conditions, medications, allergies, immunizations, and procedures.',
        tip: 'Clinical records require iOS 16+ and a supported healthcare provider connected in Apple Health.',
        warning: null,
      },
      {
        title: 'Initial sync',
        description: 'After authorization, the app syncs your recent Apple Health data to Healist. This may take a moment depending on how much data you have.',
        tip: null,
        warning: null,
      },
      {
        title: 'Ongoing sync',
        description: 'The app syncs data in the background approximately every 2 hours. You don\'t need to open it regularly \u2014 just keep it installed. Data appears in your Healist dashboard and health history.',
        tip: 'HealistHealthSync is ONLY for Apple Health data sync. All Healist features are accessed through the browser (healist.ai). The iOS app is not the main app.',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Do this first. Without HealistHealthSync, Healist can\'t see your watch or health data — so it can\'t show your trends, spot changes, or track progress on your health goals. This one step turns most of that on.' },
      { type: 'info', text: 'HealistHealthSync works on iPhone and iPad. An Android version is coming soon — until then, wearable devices can\'t connect to Healist on Android. Everything else in Healist works normally on any phone or computer, since Healist itself runs in your browser.' },
      { type: 'info', text: 'HealistHealthSync is a companion app, not the main Healist experience. Use your browser (healist.ai) for all features. The iOS app syncs Apple Health data (191 types + clinical records) that isn\'t accessible from the web.' },
    ],
  },

  // ===================== AI FEATURES =====================
  {
    id: 'dashboard-tour',
    title: 'Your Dashboard',
    icon: '\u{1F3E0}',
    category: 'getting-started',
    order: 8,
    estimatedMinutes: 4,
    description: 'Understand your health overview dashboard \u2014 cards, Today\'s Nutrition, Quick Actions, and daily workflow.',
    prerequisites: ['first-login'],
    relatedTutorials: ['healist-score', 'ai-assistant-hub'],
    tags: ['dashboard', 'overview', 'home', 'quick actions', 'daily'],
    steps: [
      {
        title: 'The Dashboard is your home base',
        description: 'When you log in, you land on the Dashboard. It shows a health overview with key metrics, your Healist Score, and quick access to common actions.',
        tip: null,
        warning: null,
      },
      {
        title: 'Health overview cards',
        description: 'Cards display your key health metrics: Healist Score, active meal plan status, recent activity, hydration, and device sync status.',
        tip: null,
        warning: null,
      },
      {
        title: 'Today\'s Nutrition',
        description: 'If you have an active meal plan, "Today\'s Nutrition" shows your meals for the day. Mark meals as eaten or skipped to track your daily intake.',
        tip: null,
        warning: null,
      },
      {
        title: 'Quick Actions',
        description: 'Six quick action buttons give fast access to common tasks: Diet Check, Log Meal, AI Assistant, What Can I Cook, and more.',
        tip: null,
        warning: null,
      },
      {
        title: 'Daily workflow',
        description: 'A typical day: check Dashboard \u2192 review today\'s meals \u2192 log meals as eaten \u2192 log hydration \u2192 check Healist Score. The Dashboard ties everything together.',
        tip: null,
        warning: null,
      },
      {
        title: 'Navigate away and back',
        description: 'Every page has a "\u2190 Dashboard" button in the header that returns you to the Dashboard. The sidebar\'s "Dashboard" item does the same.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'healist-score',
    title: 'Understanding Healist Score',
    icon: '\u{1F31F}',
    category: 'getting-started',
    order: 9,
    estimatedMinutes: 3,
    description: 'Your unified health score: 8 components, letter grades, Healist Age, and 7-day trends.',
    prerequisites: ['profile-setup', 'health-objectives'],
    relatedTutorials: ['dashboard-tour', 'xai-transparency'],
    tags: ['score', 'grade', 'healist age', 'health score', 'components'],
    steps: [
      {
        title: 'Find your Healist Score',
        description: 'Your Healist Score appears on the Dashboard and in Health \u2192 Score tab. It\'s a single number (0\u2013100) with a letter grade (A\u2013F).',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand the 8 components',
        description: 'Your score is built from: Activity (15%), Nutrition (15%), Food Quality (15%), Hydration (10%), Sleep (15%), Diet Compliance (10%), Body & Vitals (10%), and Objectives (10%).',
        tip: null,
        warning: null,
      },
      {
        title: 'View component breakdowns',
        description: 'Each component shows its individual score and grade. Low-scoring components tell you where to focus improvement efforts.',
        tip: 'Components use 7-day rolling averages, so consistent improvement over a week will raise your score.',
        warning: null,
      },
      {
        title: 'Check your Healist Age',
        description: 'Healist Age compares your health metrics to age-based benchmarks. A Healist Age lower than your actual age means you\'re healthier than average for your age.',
        tip: null,
        warning: null,
      },
      {
        title: 'Watch the 7-day trend',
        description: 'A sparkline chart shows your score trend over the last 7 days. Use this to see if you\'re improving, declining, or stable.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Healist Score is cached daily for performance. It updates when you log meals, sync devices, or enter new health data.' },
    ],
  },
  {
    id: 'ai-assistant-hub',
    title: 'AI Assistant Hub \u2014 6 Tools',
    icon: '\u{1F916}',
    category: 'ai-features',
    order: 1,
    estimatedMinutes: 3,
    description: 'Overview of all AI-powered tools and when to use each one.',
    prerequisites: ['first-login'],
    relatedTutorials: ['ai-recipe-generator', 'ai-meal-plan', 'ai-fitness-program', 'xai-transparency'],
    tags: ['ai', 'assistant', 'hub', 'tools', 'chat'],
    steps: [
      {
        title: 'Open the AI Assistant Hub',
        description: 'Tap "AI Consult" at the top of the sidebar, or use the "AI Assistant" button in the header. This opens the hub with all AI tools.',
        tip: null,
        warning: null,
      },
      {
        title: 'AI Health Advisor Chat',
        description: 'Have a conversation with your AI Health Advisor. Ask about diet, nutrition, supplements, fitness, or get personalized advice based on your complete health profile.',
        tip: null,
        warning: null,
      },
      {
        title: 'Recipe Generator & Optimizer',
        description: 'Generate new recipes or optimize existing ones. The AI considers your allergies, objectives, and dietary preferences.',
        tip: null,
        warning: null,
      },
      {
        title: 'Meal Plan & Fitness Program Generators',
        description: 'Create comprehensive weekly meal plans or multi-week fitness programs, both personalized to your health data.',
        tip: 'These are the most credit-intensive actions in Healist — a fitness program costs 12 credits and meal plans run 15 to 80 depending on length, so you will be asked to confirm before one runs. See "Credits — How AI Actions Are Priced".',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'All AI features include XAI (Explainable AI) transparency. Tap "📚 Citations & AI Reasoning" on any AI output to see its published sources and exactly what data drove the recommendation.' },
    ],
  },
  {
    id: 'xai-transparency',
    title: 'Citations & AI Reasoning',
    icon: '\u{1F4DA}',
    category: 'ai-features',
    order: 2,
    estimatedMinutes: 2,
    description: 'Find the published sources behind any health guidance, and see how the AI reached its answer.',
    prerequisites: ['ai-assistant-hub'],
    relatedTutorials: ['ai-recipe-generator', 'ai-meal-plan', 'ai-fitness-program'],
    tags: ['citations', 'sources', 'xai', 'explainability', 'transparency', 'reasoning', 'prompt'],
    steps: [
      {
        title: 'Find the Citations button',
        description: 'On any AI-generated content (recipes, meal plans, fitness programs, chat responses, Healist Score, trends), tap the "📚 Citations & AI Reasoning" button. On safety checks — medication interactions, timing, shopping-list drug badges, allergen and compatibility checks — tap the "📚 Citations" chip on the item. The Citations tab opens first.',
        tip: null,
        warning: null,
      },
      {
        title: 'Layer 1: Summary',
        description: 'The Summary panel shows 17 Unified Context pattern factors (allergies, objectives, medications, etc.) with importance bars indicating how strongly each factor influenced the output.',
        tip: null,
        warning: null,
      },
      {
        title: 'Layer 2: Full Prompt',
        description: 'See the exact system prompt, user prompt, and model parameters sent to the AI. This is full transparency \u2014 nothing hidden.',
        tip: 'The full prompt shows your Unified Health Context (UHC) data. This is the same data shown under Records \u2192 My UHC Data Set.',
        warning: null,
      },
      {
        title: 'Citations (opens first)',
        description: 'The Citations tab lists the published sources: first any warnings from authorities about this result (on Longevity options, for example an FDA boxed warning), then the sources for this specific result (for example the FDA drug label or NIH fact sheet behind an interaction warning), then the standards the check is built on. Every citation links to the publishing authority.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'XAI transparency is a core Healist principle. Every AI recommendation should be explainable and traceable to your health data.' },
    ],
  },

  {
    id: 'analytics-reporting',
    title: 'Analytics & Reporting',
    icon: '\u{1F4C8}',
    category: 'ai-features',
    order: 3,
    estimatedMinutes: 4,
    description: 'Read your health KPIs, explore trends by domain, and generate an AI trend analysis across all six health domains.',
    prerequisites: ['first-login'],
    relatedTutorials: ['dashboard-tour', 'healist-score', 'wearable-devices', 'xai-transparency'],
    tags: ['analytics', 'reporting', 'trends', 'kpi', 'reports', 'steps', 'sleep', 'hrv'],
    steps: [
      {
        title: 'Open Analytics & Reporting',
        description: 'Tap Analytics & Reporting in the sidebar, under the HOME section — directly below Dashboard.',
        tip: null,
        warning: null,
      },
      {
        title: 'Pick your time range',
        description: 'Use the range buttons at the top — 7, 14, 30 or 90 days. 30 days is the default. Every KPI tile and trend below reflects the range you choose, so set it before you read anything.',
        tip: null,
        warning: null,
      },
      {
        title: 'Read your KPI tiles',
        description: 'The tiles summarize the selected range: average steps, calories, sleep and hydration, plus resting heart rate, latest weight, average HRV and your Healist Score. Each tile shows its unit (weight in the unit you chose in your body profile) and, when there is data for both, the change versus the previous period of the same length. A tile showing "--" simply means there is no data for that metric yet in that range.',
        tip: 'Steps, sleep, resting heart rate and HRV come from your connected wearable — see "Connect Wearable Devices" if these are empty.',
        warning: null,
      },
      {
        title: 'Explore trends by domain',
        description: 'The Trends tab breaks your data into six domains — Overview, Nutrition, Activity, Sleep, Body and Vitals. Move between them to see each domain on its own rather than as one blended chart.',
        tip: null,
        warning: null,
      },
      {
        title: 'Generate an AI trend analysis',
        description: 'Tap "Generate AI Trend Analysis" (1 credit). The AI reviews all six health domains in a single pass for your selected time range and tells you what is actually changing — and what it means in the context of your full health profile.',
        tip: 'This is one of the cheapest AI actions in Healist at 1 credit. Re-reading an analysis you already generated is free.',
        warning: null,
      },
      {
        title: 'Check the reasoning',
        description: 'Like every AI output in Healist, a trend analysis is explainable — open its AI reasoning to see exactly which of your data drove each conclusion.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Analytics reads the same Unified Health Context as the rest of Healist, so anything you log, upload, or sync shows up here automatically — there is nothing to import.' },
    ],
  },

  // ===================== ACCOUNT & SETTINGS =====================
  {
    id: 'hydration-tracking',
    title: 'Track Hydration',
    icon: '\u{1F4A7}',
    category: 'nutrition',
    order: 10,
    estimatedMinutes: 2,
    description: 'Log daily water intake, set goals, and customize glass sizes.',
    prerequisites: ['first-login'],
    relatedTutorials: ['healist-score', 'settings-notifications'],
    tags: ['hydration', 'water', 'drinking', 'glasses', 'daily goal'],
    steps: [
      {
        title: 'Navigate to Hydration settings',
        description: 'Go to Settings in the sidebar. The "Hydration" tab lets you configure your daily water goal and glass sizes.',
        tip: null,
        warning: null,
      },
      {
        title: 'Set your daily water goal',
        description: 'Set a target number of glasses per day. The default is 8 glasses. Adjust based on your body weight, activity level, and climate.',
        tip: null,
        warning: null,
      },
      {
        title: 'Customize glass size',
        description: 'Set your standard glass size in ounces. This converts glass count to total fluid ounces for more accurate tracking.',
        tip: null,
        warning: null,
      },
      {
        title: 'Log water on the Dashboard',
        description: 'The Dashboard shows a hydration tracker. Tap to add glasses throughout the day. Your hydration contributes 10% to your Healist Score.',
        tip: 'Set up push notifications for hydration reminders to help you meet your daily goal.',
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'meal-logging',
    title: 'Daily Meal Logging',
    icon: '\u{1F37D}\u{FE0F}',
    category: 'nutrition',
    order: 11,
    estimatedMinutes: 3,
    description: 'Mark meals as eaten or skipped, add manual entries, and close your day.',
    prerequisites: ['ai-meal-plan'],
    relatedTutorials: ['diet-compliance', 'healist-score'],
    tags: ['meals', 'logging', 'eaten', 'skipped', 'daily'],
    steps: [
      {
        title: 'Find today\'s meals',
        description: 'On the Dashboard, the "Today\'s Nutrition" section shows meals from your active meal plan for today.',
        tip: null,
        warning: null,
      },
      {
        title: 'Mark meals as eaten',
        description: 'Tap a meal and mark it as "Eaten" to log that you consumed it. The nutrition totals update automatically.',
        tip: null,
        warning: null,
      },
      {
        title: 'Skip a meal',
        description: 'If you didn\'t eat a planned meal, mark it as "Skipped." This is tracked separately from eaten meals for compliance analysis.',
        tip: null,
        warning: null,
      },
      {
        title: 'Add a manual meal entry',
        description: 'Ate something not on your plan? Use "Log Meal" from Dashboard Quick Actions to add a manual entry with food items and approximate nutrition.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review daily nutrition totals',
        description: 'Today\'s Nutrition shows running totals for calories, protein, carbs, and fat. Compare against your daily targets.',
        tip: 'Consistent meal logging improves your Diet Compliance score and gives the AI better data for future meal plan generation.',
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'multi-device-sessions',
    title: 'Multi-Device Sessions',
    icon: '\u{1F4F1}',
    category: 'account',
    order: 1,
    estimatedMinutes: 2,
    description: 'View active sessions across devices, remote logout, and understand session limits.',
    prerequisites: ['first-login'],
    relatedTutorials: ['data-privacy'],
    tags: ['sessions', 'devices', 'logout', 'security', 'multi-device'],
    steps: [
      {
        title: 'Navigate to Active Sessions',
        description: 'Go to Account in the sidebar. The "Active Sessions" card shows all devices currently logged into your account.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review your sessions',
        description: 'Each session shows device type (desktop/mobile), device name, and last activity. Your current session is marked.',
        tip: null,
        warning: null,
      },
      {
        title: 'Remote logout',
        description: 'Use "Logout This Device" to revoke a specific session, or "Logout All Others" to sign out all devices except your current one. Revocation is immediate.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand session limits',
        description: 'A maximum of 5 concurrent sessions is enforced. When you log in on a 6th device, the oldest session is automatically revoked. You\'ll receive a push notification on other devices.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Session revocation is immediate \u2014 revoked sessions receive a 401 error on their next API call and are redirected to the login screen.' },
    ],
  },
  {
    id: 'settings-notifications',
    title: 'Settings & Notifications',
    icon: '\u{2699}\u{FE0F}',
    category: 'account',
    order: 2,
    estimatedMinutes: 3,
    description: 'Configure hydration and meal goals, push notification toggles, quiet hours, and app preferences.',
    prerequisites: ['first-login'],
    relatedTutorials: ['hydration-tracking', 'meal-logging'],
    tags: ['settings', 'notifications', 'push', 'quiet hours', 'preferences'],
    steps: [
      {
        title: 'Navigate to Settings',
        description: 'Go to Settings in the sidebar. The page has 3 tabs: Hydration, Meals, and Configuration Guides.',
        tip: null,
        warning: null,
      },
      {
        title: 'Configure hydration settings',
        description: 'Set daily glass goal, glass size, and hydration reminder frequency.',
        tip: null,
        warning: null,
      },
      {
        title: 'Configure meal settings',
        description: 'Set daily meal targets and meal timing preferences.',
        tip: null,
        warning: null,
      },
      {
        title: 'Manage push notifications',
        description: 'In Account → Settings → Notifications, switch each of 9 notification types on or off: hydration reminders, meal alerts, medication reminders, goal achievements, weekly summary, pantry expiration, provider messages, device sync alerts, and Health Alerts. Health Alerts tell you when a reading is well outside your own usual range (by push and email); they compare with your own 14-day history, are not a diagnosis and are not real-time monitoring, are off until you turn them on, and wait until your quiet hours end. A reading FAR outside your usual range always appears in your notification center (the bell), even with Health Alerts off.',
        tip: 'On a computer, many reminders carry action buttons (in Chrome, hover the notification and click Options) — log water, mark a meal eaten, or answer a reached goal without opening Healist. They act on your account, so you must be signed in to Healist in that browser; if your session has expired, sign in once and they work again.',
        warning: 'iPhone/iPad: two Apple limits to know. (1) Enable notifications only from the app opened via its Home Screen icon — turning them on inside a Safari tab does nothing. (2) Apple does not show action buttons on web notifications at all, so on iPhone you tap the reminder and Healist opens at the right screen to log it there.',
      },
      {
        title: 'Set quiet hours',
        description: 'Configure quiet hours (e.g., 10pm\u20137am) to suppress all push notifications during sleep or focus time.',
        tip: null,
        warning: null,
      },
    ],
    notes: [],
  },
  {
    id: 'data-privacy',
    title: 'Data Privacy & Account',
    icon: '\u{1F512}',
    category: 'account',
    order: 3,
    estimatedMinutes: 3,
    description: 'Understand HIPAA protections, delete your account, and know what gets permanently removed.',
    prerequisites: ['first-login'],
    relatedTutorials: ['multi-device-sessions', 'medical-records'],
    tags: ['privacy', 'hipaa', 'delete account', 'data', 'security', 'gdpr'],
    steps: [
      {
        title: 'Understand HIPAA protection',
        description: 'Healist is HIPAA-compliant. All medical data is encrypted at rest (S3 SSE-KMS) and in transit (TLS). Access is logged in an immutable audit trail.',
        tip: null,
        warning: null,
      },
      {
        title: 'View what data Healist stores',
        description: 'Go to Records \u2192 My UHC Data Set to see the health data stored in your profile. This is the complete picture of what Healist knows about you.',
        tip: null,
        warning: null,
      },
      {
        title: 'Review audit logging',
        description: 'Every access to your health data is logged: who accessed it, when, and what they did. This is a HIPAA requirement that protects your privacy.',
        tip: null,
        warning: null,
      },
      {
        title: 'Delete your account',
        description: 'In Account settings, use "Delete Account" to permanently remove your account and all associated data. This action is irreversible.',
        tip: null,
        warning: 'Account deletion is permanent and irreversible. All health data, recipes, meal plans, fitness programs, medical records, and device connections are permanently deleted via database CASCADE.',
      },
      {
        title: 'What gets deleted',
        description: 'Everything: profile, health data, recipes, meal plans, fitness programs, medical records (files + extracted data), device connections, session data, notification tokens, and audit logs. The only exception is anonymized referral records.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Healist follows the "right to be forgotten" principle. Every foreign key referencing your user ID has ON DELETE CASCADE, ensuring complete data removal.' },
    ],
  },

  // ===================== DIGITAL TWIN =====================
  {
    id: 'digital-twin-overview',
    title: 'Digital Twin Overview',
    icon: '\u{1F9EC}',
    category: 'digital-twin',
    order: 1,
    estimatedMinutes: 4,
    description: 'Understand what your Digital Twin is, how it works, and what each tab shows you.',
    prerequisites: ['first-login', 'profile-setup'],
    relatedTutorials: ['twin-simulations', 'twin-patterns', 'twin-schedule'],
    tags: ['digital twin', 'overview', 'health state', 'baselines', 'anomalies', 'metrics'],
    steps: [
      {
        title: 'Navigate to Digital Twin',
        description: 'In the sidebar menu under Medical, tap "Digital Twin." The page opens on the Overview tab and automatically syncs your latest health data.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand the Twin Status card',
        description: 'The top card shows: how many health metrics are being tracked, active cross-domain patterns, active anomaly alerts, and the last sync time. Data freshness indicators show how recently each domain (Biometric, Nutritional, Behavioral) received new data.',
        tip: 'The twin auto-syncs when you open the page. You can also tap the Sync button to force a refresh.',
        warning: null,
      },
      {
        title: 'Read your Rolling Baselines',
        description: 'Below the status card, the Rolling Baselines section shows your personalized 14-day averages grouped by domain (Biometric, Nutritional, Behavioral). Each metric shows the mean \u00B1 standard deviation and sample count. These baselines are what the AI compares your current readings against to detect anomalies.',
        tip: 'Baselines are recomputed nightly. The more consistently you log data, the more accurate your baselines become.',
        warning: null,
      },
      {
        title: 'Review Active Alerts',
        description: 'When a reading is outside your own usual range (compared with your 14-day average), a card appears on the Overview tab under "Readings outside your usual range". Each card shows your reading, your average and a short plain-language note; it does not diagnose anything or mention medications. You can dismiss cards you\'ve reviewed.',
        tip: null,
        warning: null,
      },
      {
        title: 'Use the Quick Action pills',
        description: 'The pill toolbar at the top provides shortcuts: "View Tutorials" brings you here, "Run Simulation" opens the what-if simulator, "Discover Patterns" analyzes cross-domain correlations, and "Optimize Schedule" generates medication timing proposals.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Your Digital Twin is a continuous representation of your health state built from all your logged data \u2014 nutrition, biometrics, activity, and medications. It\u2019s the foundation for all AI-powered predictions, pattern discovery, and schedule optimization.' },
    ],
  },
  {
    id: 'twin-simulations',
    title: 'What-If Simulations',
    icon: '\u{1F52E}',
    category: 'digital-twin',
    order: 2,
    estimatedMinutes: 5,
    description: 'Run AI-powered "what if" predictions to see how health changes could affect your metrics over time.',
    prerequisites: ['digital-twin-overview'],
    relatedTutorials: ['twin-patterns', 'twin-schedule'],
    tags: ['simulation', 'what if', 'prediction', 'diet', 'medication', 'exercise', 'supplement', 'schedule', 'tracking', 'accuracy'],
    steps: [
      {
        title: 'Choose a simulation category',
        description: 'On the Simulations tab, choose from 5 categories: Diet (change eating patterns), Medication (adjust medications), Exercise (change workout routine), Supplement (add or change supplements), or Schedule (change medication/meal timing).',
        tip: null,
        warning: null,
      },
      {
        title: 'Configure your scenario',
        description: 'A modal opens for you to describe the change. For example, for a diet simulation you might enter "keto" or "Mediterranean." For medication, describe the adjustment. Set the prediction horizon (7, 14, or 30 days).',
        tip: 'Be specific in your descriptions \u2014 the AI uses your actual health profile, medications, and conditions to generate personalized predictions.',
        warning: null,
      },
      {
        title: 'Review AI predictions',
        description: 'The results show: predicted metric changes with confidence intervals, impact by health domain (nutrition, metabolic, cardiovascular, etc.), Healist Score projection, AI analysis with actionable recommendations, and potential risks. An XAI Transparency panel shows exactly what data the AI used.',
        tip: null,
        warning: 'Simulations are AI predictions, not medical advice. Always consult your healthcare provider before making changes to medications or treatment plans.',
      },
      {
        title: 'Track a simulation',
        description: 'Click "Track This" on a completed simulation to begin comparing the AI\u2019s predictions against your actual health data over the horizon period. The system monitors your real metrics and computes an accuracy score when the tracking window closes.',
        tip: 'Tracking results feed back into the AI as few-shot examples, making future simulations more accurate for you specifically. The more simulations you track, the smarter your Digital Twin becomes.',
        warning: null,
      },
      {
        title: 'View tracking progress',
        description: 'Click the "TRACKING" badge on an active simulation to see a progress bar, days remaining, start/end dates, and the original predictions. When the tracking window closes, you\u2019ll see a prediction accuracy percentage.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Simulations consider your complete health context: current medications, conditions, health goals, recent trends, and even your pantry contents for dietary scenarios. The AI never diagnoses conditions or contradicts prescriptions.' },
    ],
  },
  {
    id: 'twin-patterns',
    title: 'Cross-Domain Pattern Discovery',
    icon: '\u{1F50D}',
    category: 'digital-twin',
    order: 3,
    estimatedMinutes: 4,
    description: 'Discover hidden correlations between your nutrition, biometrics, activity, and medications.',
    prerequisites: ['digital-twin-overview'],
    relatedTutorials: ['twin-simulations'],
    tags: ['patterns', 'correlation', 'discovery', 'cross-domain', 'biometric', 'nutritional', 'behavioral', 'pharmaceutical'],
    steps: [
      {
        title: 'Run pattern discovery',
        description: 'On the Patterns tab, click "Discover Patterns." The AI analyzes your health data across all domains, computing time-lagged correlations at 1-hour increments from 0 to 72 hours. This identifies relationships like "high sugar intake leads to elevated glucose 2 hours later."',
        tip: 'You need at least 30 days of health data for meaningful pattern discovery. The more data you have, the stronger the patterns.',
        warning: null,
      },
      {
        title: 'Understand pattern cards',
        description: 'Each discovered pattern shows: the two related metrics (e.g., sodium intake \u2192 blood pressure), the time lag between them, correlation strength displayed as a color-coded bar, the direction of influence (which metric leads), and the lifecycle state.',
        tip: null,
        warning: null,
      },
      {
        title: 'Filter by domain',
        description: 'Use the domain filter buttons (All, Biometric, Nutritional, Behavioral, Pharmaceutical) to focus on specific types of patterns. This is helpful when you have many discovered patterns.',
        tip: null,
        warning: null,
      },
      {
        title: 'View pattern details',
        description: 'Click a pattern card to open a detail modal with: the full AI-generated narrative explaining the correlation in plain English, validation history, pharmaceutical context (if medications are involved), and statistical details (p-value, confidence level).',
        tip: 'Patterns describe correlations, not causation. "A correlates with B" doesn\u2019t necessarily mean A causes B. Discuss significant patterns with your healthcare provider.',
        warning: null,
      },
      {
        title: 'Pattern lifecycle',
        description: 'Patterns progress through states: Discovery (newly found) \u2192 Active (validated) \u2192 Strengthening or Weakening (based on ongoing validation) \u2192 Archived (no longer significant). The system automatically validates patterns nightly and archives those that degrade.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Pattern discovery uses Bonferroni-corrected statistical analysis with holdout validation. Only patterns that pass rigorous statistical testing are surfaced. Discovered patterns also improve what-if simulation accuracy over time.' },
    ],
  },
  {
    id: 'twin-schedule',
    title: 'Medication & Supplement Scheduling',
    icon: '\u{23F0}',
    category: 'digital-twin',
    order: 4,
    estimatedMinutes: 5,
    description: 'Optimize when you take medications and supplements to minimize drug-nutrient interactions.',
    prerequisites: ['digital-twin-overview'],
    relatedTutorials: ['twin-simulations', 'medications-supplements'],
    tags: ['schedule', 'medication', 'supplement', 'timing', 'pharmacokinetic', 'pk', 'interactions', 'drug-nutrient', 'optimization'],
    steps: [
      {
        title: 'View your PK profiles',
        description: 'The Schedule tab shows Pharmacokinetic (PK) Profiles for each of your medications and supplements. Each card shows onset, time to peak concentration, half-life, therapeutic index (narrow/moderate/wide) and how food affects the medicine \u2014 each in the words of its source, for example "6 to 12 hours" or "With or without food". Where the source says nothing about a value, the card says "Not stated in the label". The source line names the label and the product form it covers (for example "FDA-approved drug label \u2014 tablet (Synthroid)") with a citation you can open.',
        tip: 'PK profiles come from FDA drug labels and NIH fact sheets, checked word for word. Spacing rules follow the label exactly, including direction \u2014 for example "take 2 hours before or 6 hours after antacids". Healist only warns about timing the source actually states. If no verified source exists for an item, the card says "No verified timing data on file", and an older estimate is labelled "Estimate \u2014 not verified" and is never used for your schedule. Ask your pharmacist or clinician about timing for those items.',
        warning: null,
      },
      {
        title: 'Optimize your schedule',
        description: 'Click "Optimize Schedule" to generate personalized timing proposals. The system analyzes your medications, supplements, typical meal times, and known drug-nutrient interactions to create an optimized daily schedule that minimizes interference.',
        tip: 'You don’t have to log every meal or dose. Times come from your own logs when there are enough of them, otherwise from your reminder times and meal plan (or standard defaults), all in your local time. Setting reminder times makes the schedule fit your day.',
        warning: null,
      },
      {
        title: 'Compare schedule proposals',
        description: 'The optimizer generates 2+ proposals ranked by interference score (lower is better). Each proposal shows: a visual 24-hour timeline with medication windows, meal windows, and supplement slots; resolved interactions; residual conflicts; adherence difficulty rating; and an AI explanation.',
        tip: 'Look at the adherence difficulty rating \u2014 the "best" schedule isn\u2019t helpful if it\u2019s too complex to follow consistently.',
        warning: null,
      },
      {
        title: 'Toggle weekday vs weekend',
        description: 'Use the Weekday/Weekend toggle to see separate schedule proposals. Your routine often differs on weekends (later meals, different activity levels), so the optimizer creates distinct schedules for each.',
        tip: null,
        warning: null,
      },
      {
        title: 'Activate a schedule',
        description: 'Choose the proposal that works best for your lifestyle and click to activate it. The activated schedule becomes your reference for daily timing. You can re-optimize anytime your medications, supplements, or routine changes.',
        tip: null,
        warning: 'Schedule optimization is based on general pharmacokinetic data. Individual responses may vary. Always follow your prescriber\u2019s specific instructions for medication timing.',
      },
    ],
    notes: [
      { type: 'info', text: 'The scheduling algorithm prioritizes narrow therapeutic index drugs first (where timing matters most), then arranges meals, remaining prescriptions, and supplements around them. Drug-nutrient interactions like chelation, pH alteration, and enzyme competition are all considered.' },
    ],
  },

  // ===================== LONGEVITY =====================
  {
    id: 'longevity-overview',
    title: 'Longevity Intelligence Overview',
    icon: '\u{2728}',
    category: 'longevity',
    order: 1,
    estimatedMinutes: 4,
    description: 'What Longevity Intelligence does, how its five tabs fit together, and which plans include which parts.',
    prerequisites: ['first-login'],
    relatedTutorials: ['credits-explained', 'digital-twin-overview', 'healist-score'],
    tags: ['longevity', 'biological age', 'anti-aging', 'phenoage', 'healthspan', 'overview'],
    steps: [
      {
        title: 'Open Longevity',
        description: 'Tap Longevity in the sidebar, under the MEDICAL section. The page opens on Overview and shows your current status — "Not yet assessed" until you run your first assessment.',
        tip: null,
        warning: null,
      },
      {
        title: 'Understand what it is for',
        description: 'Longevity Intelligence is your personalized anti-aging engine: it assesses how your body is actually aging, ranks the highest-return changes you could make, and lets you optimize from there. It reads your labs, body composition and device data — the same Unified Health Context that powers the rest of Healist.',
        tip: null,
        warning: null,
      },
      {
        title: 'Learn the five tabs',
        description: 'Overview (ASSESS) — your biological age and what is driving it. Interventions (RANK) — your ranked, safety-filtered list of what to change. Simulate — project your trajectory 5, 10 and 20 years out (Max plans). Regimen — a concrete plan plus a clinical safety briefing. History — your biological age over time.',
        tip: 'The Overview tab is marked FREE — browsing it never costs credits.',
        warning: null,
      },
      {
        title: 'Know what your plan includes',
        description: 'Longevity is not included on Base. Pro includes assessment, ranking, per-item guidance and recalibration. Max adds the Simulate family — trajectory simulation, multi-scenario comparison and auto-recalibration detection. Your free trial includes the full feature set so you can evaluate it.',
        tip: 'The Interventions tab includes a "Know vs Optimize — three ways Max goes further" panel that spells out exactly what upgrading adds.',
        warning: 'A credit pack raises how much you can run on your current plan — it never unlocks a higher plan’s features.',
      },
      {
        title: 'Read the disclaimer',
        description: 'Every Longevity view carries a standing notice: this is educational, informational guidance — NOT medical advice, diagnosis, or treatment. All interventions, clinical and aesthetic, must be confirmed with your licensed provider before you act on them.',
        tip: null,
        warning: 'Never start, stop, or change a medication or supplement based on a Longevity output alone. Confirm with your provider first.',
      },
    ],
    notes: [
      { type: 'info', text: 'Navigating and viewing Longevity is always free. Credits are spent only when you run an assessment, ranking or simulation. Building a regimen and its safety briefing is free.' },
    ],
  },
  {
    id: 'longevity-assess',
    title: 'Assess Your Biological Age',
    icon: '\u{1F9EA}',
    category: 'longevity',
    order: 2,
    estimatedMinutes: 5,
    description: 'Run your first biological-age assessment, understand your three headline numbers, and see what is driving your aging.',
    prerequisites: ['longevity-overview'],
    relatedTutorials: ['medical-records', 'wearable-devices', 'body-characteristics'],
    tags: ['biological age', 'phenoage', 'assessment', 'bia', 'vo2max', 'confidence'],
    steps: [
      {
        title: 'Get your inputs in place first',
        description: 'The assessment reads three sources: a lab panel (PhenoAge markers on file), body composition (BIA), and device data (VO₂max, resting heart rate, sleep). The more of these you have, the tighter the result.',
        tip: 'Upload labs under Records → Upload Medical Records, and connect a wearable under Devices. Both feed this assessment automatically.',
        warning: null,
      },
      {
        title: 'Run the assessment',
        description: 'On the Overview tab, tap "Assess my biological age". It is free and takes a few moments while Healist decomposes your aging drivers.',
        tip: null,
        warning: null,
      },
      {
        title: 'Missing markers are handled honestly',
        description: 'You do not need a complete data set to start. Anything missing is noted in your confidence score rather than silently guessed — so you always know how much weight to put on the number.',
        tip: null,
        warning: null,
      },
      {
        title: 'Read your three numbers',
        description: 'The result explains "How your three numbers fit together" — your chronological age, your biological age, and how fast you are currently aging. The relationship between them, not any single figure, is the signal.',
        tip: null,
        warning: null,
      },
      {
        title: 'See what is driving your aging',
        description: 'Below your numbers, the "What\'s driving your aging" panel decomposes the result into the specific contributors pushing your biological age up or down. This is what the Interventions tab then ranks.',
        tip: null,
        warning: null,
      },
      {
        title: 'Re-assess when something changes',
        description: 'After new labs or a meaningful change in your routine, use Recalibrate (free) to re-run the assessment. Each result is retained, so History builds a real trend line rather than a single snapshot.',
        tip: 'On Max plans, Healist also detects when a recalibration is worth running — that detection is free; you decide whether to spend.',
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'Viewing an assessment you already paid for is always free, however many times you return to it.' },
    ],
  },
  {
    id: 'longevity-interventions',
    title: 'Rank Interventions & Build a Regimen',
    icon: '\u{1F3AF}',
    category: 'longevity',
    order: 3,
    estimatedMinutes: 5,
    description: 'Turn your assessment into a ranked, safety-filtered action list and a concrete regimen with a clinical safety briefing.',
    prerequisites: ['longevity-assess'],
    relatedTutorials: ['medications-supplements', 'provider-coordination'],
    tags: ['interventions', 'ranking', 'regimen', 'safety', 'supplements', 'briefing'],
    steps: [
      {
        title: 'Rank your interventions',
        description: 'Open the Interventions tab and tap to rank (3 credits). This runs the full cross-domain engine — nutrition, fitness, sleep, supplements, medications and more — and returns "Your ranked interventions", ordered by expected return for YOUR profile.',
        tip: null,
        warning: null,
      },
      {
        title: 'Trust the safety filter',
        description: 'A "Filtered for your safety" panel shows what was deliberately withheld and why — interventions screened out against your medications, conditions and allergies. Nothing is recommended that conflicts with your own health context.',
        tip: null,
        warning: null,
      },
      {
        title: 'Check the evidence and the warnings',
        description: 'Every intervention card shows its evidence level and two controls right under it. "📚 Citations" opens the published evidence for that intervention (an NIH fact sheet, an FDA label, a study or a review) plus the sources of any safety flag. "⚠️ Authority warnings" opens what regulators and health authorities warn about it — for example an FDA boxed warning or consumer alert — each linked to its source. Where a cited source has an industry tie, a conflict-of-interest note says so.',
        tip: 'Healist re-checks every one of these sources each week, so a citation that moves or stops saying what it is cited for is caught and fixed.',
        warning: 'A warning is not a ranking penalty — options are kept so you can see them with their risks. Discuss any warned option with your provider before acting.',
      },
      {
        title: 'Dig into any single item',
        description: 'Each intervention card has a free "Why?" that shows why it was ranked where it was and what the safety checks found. Cards flagged ⚠️ Caution also have a free "Brief" that builds the clinical safety briefing for just that one item.',
        tip: null,
        warning: null,
      },
      {
        title: 'Build your regimen',
        description: 'On the Regimen tab, tap "Build regimen & briefing" (free) to turn the ranked list into a plan. Doses are never written by the AI: you see a cited amount where an authority states one (for example the U.S. Physical Activity Guidelines or NIH sleep guidance), "Discuss the dose with your clinician" where none does, and "Set by your clinician" for prescription, unapproved or supervised options. The Clinical Safety Briefing is built only from your safety checks and the cited authority warnings — each note with its own source — plus questions to take to your provider.',
        tip: 'Returning to a regimen you already built is free — the Regimen tab shows a "View Last Regimen — FREE" option.',
        warning: null,
      },
      {
        title: 'Take it to your provider',
        description: 'The Clinical Safety Briefing exists so this is a conversation with your clinician, not a substitute for one. Bring it to your provider and confirm before you change anything.',
        tip: 'Physicians and providers can be recorded under Health → Physicians / Providers so your context stays complete.',
        warning: 'Longevity output is educational guidance only — not medical advice, diagnosis, or treatment. Confirm every intervention with your licensed provider first.',
      },
    ],
    notes: [],
  },
  {
    id: 'longevity-simulate',
    title: 'Simulate Your Trajectory (Max)',
    icon: '\u{1F4C8}',
    category: 'longevity',
    order: 4,
    estimatedMinutes: 4,
    description: 'Project your biological age years into the future, compare scenarios side by side, and track the trend over time.',
    prerequisites: ['longevity-interventions'],
    relatedTutorials: ['twin-simulations', 'credits-explained'],
    tags: ['simulate', 'trajectory', 'scenarios', 'max', 'projection', 'history'],
    steps: [
      {
        title: 'Check that your plan includes it',
        description: 'Simulate is a Max feature (and is included in your free trial). On Pro it appears but is gated — the Interventions tab explains what Max adds.',
        tip: null,
        warning: null,
      },
      {
        title: 'Project your biological age',
        description: 'On the Simulate tab, pick the interventions you would realistically adopt and run "Simulate this plan" (4 credits). Healist projects your biological-age trajectory 5, 10 and 20 years out.',
        tip: null,
        warning: null,
      },
      {
        title: 'Compare scenarios side by side',
        description: 'Build more than one plan — Scenario A, Scenario B and so on — and compare them (2 credits) to see which combination actually moves your trajectory most. This is the difference between knowing your risks and optimizing against them.',
        tip: null,
        warning: null,
      },
      {
        title: 'Watch the real trend in History',
        description: 'The History tab plots "Your biological age over time" from your actual assessments. A simulation is a projection; History is the record of what really happened — check it as you recalibrate.',
        tip: null,
        warning: null,
      },
    ],
    notes: [
      { type: 'info', text: 'A simulation is a model of a possible future under stated assumptions, not a prediction of your outcome. Treat the direction and relative size of the effect as the useful signal.' },
    ],
  },

];
